import React, { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import {
    mergeScheduleResults,
    validateStepsChronological,
    validateDeadline,
    validateStartDate,
} from 'components/shared/production-scheduling/production-scheduling.utils';
import { useLazyQuery } from '@apollo/client';
import {
    autoScheduleProductionPlanTemplate,
    calculateStepHoursTemplate,
    updateStepWithCascadeTemplate,
} from 'apollo/templates/order';
import {
    AutoScheduleInput,
    AutoScheduleStepResult,
    CalculateStepHoursResult,
    CalculateStepHoursStepInput,
    UpdateStepWithCascadeInput,
    ProductionPlanningStep,
    AutoScheduleDirection,
    ChangedProperty,
} from 'models/production-planning';

type ProductionPlanningContextType = {
    // GraphQL operations
    autoScheduleProductionPlan: (input: AutoScheduleInput) => Promise<AutoScheduleStepResult[] | undefined>;
    calculateStepHours: (
        steps: CalculateStepHoursStepInput[],
        startDate?: Date,
    ) => Promise<CalculateStepHoursResult[] | undefined>;
    updateStepWithCascade: (input: UpdateStepWithCascadeInput) => Promise<AutoScheduleStepResult[] | undefined>;

    // Helper functions
    runFullAutoSchedule: (
        steps: ProductionPlanningStep[],
        direction: AutoScheduleDirection,
        startDate: Date | null,
        internalDeliveryDate: Date | null | undefined,
    ) => Promise<ProductionPlanningStep[]>;
    handleStepChangeWithCascade: (
        steps: ProductionPlanningStep[],
        index: number,
        update: Partial<ProductionPlanningStep>,
    ) => Promise<ProductionPlanningStep[]>;
    mergeScheduleResults: (
        steps: ProductionPlanningStep[],
        results: AutoScheduleStepResult[],
    ) => ProductionPlanningStep[];

    // Validation helpers
    validateStepsChronological: (steps: ProductionPlanningStep[]) => boolean;
    validateDeadline: (deadline: Date | null, internalDeliveryDate: Date | null | undefined) => boolean;
    validateStartDate: (step: ProductionPlanningStep, index: number) => boolean;

    // Loading states
    autoScheduling: boolean;
    calculatingHours: boolean;
    updatingWithCascade: boolean;
};

const ProductionPlanningContext = createContext<ProductionPlanningContextType | undefined>(undefined);

export function ProductionPlanningProvider({
    children,
    excludeOrderId,
}: {
    children: ReactNode;
    excludeOrderId?: string;
}) {
    const [autoScheduleMutation, { loading: autoScheduling }] = useLazyQuery(autoScheduleProductionPlanTemplate, {
        fetchPolicy: 'no-cache',
    });

    const [calculateHoursMutation, { loading: calculatingHours }] = useLazyQuery(calculateStepHoursTemplate, {
        fetchPolicy: 'no-cache',
    });

    const [updateStepWithCascadeMutation, { loading: updatingWithCascade }] = useLazyQuery(
        updateStepWithCascadeTemplate,
        {
            fetchPolicy: 'no-cache',
        },
    );

    // ============================================================================
    // GraphQL Operations
    // ============================================================================

    const autoScheduleProductionPlan = useCallback(
        async (input: AutoScheduleInput): Promise<AutoScheduleStepResult[] | undefined> => {
            try {
                const result = await autoScheduleMutation({ variables: { input, excludeOrderId } });
                return result.data?.autoScheduleProductionPlan;
            } catch (error) {
                console.error('Error auto-scheduling production plan:', error);
                throw error;
            }
        },
        [autoScheduleMutation, excludeOrderId],
    );

    const calculateStepHours = useCallback(
        async (
            steps: CalculateStepHoursStepInput[],
            startDate?: Date,
        ): Promise<CalculateStepHoursResult[] | undefined> => {
            try {
                const input = {
                    steps: steps.filter((s) => s.deadline),
                    startDate,
                };
                const result = await calculateHoursMutation({ variables: { input } });
                return result.data?.calculateStepHours;
            } catch (error) {
                console.error('Error calculating step hours:', error);
                throw error;
            }
        },
        [calculateHoursMutation],
    );

    const updateStepWithCascade = useCallback(
        async (input: UpdateStepWithCascadeInput): Promise<AutoScheduleStepResult[] | undefined> => {
            try {
                const result = await updateStepWithCascadeMutation({ variables: { input, excludeOrderId } });
                return result.data?.updateStepWithCascade;
            } catch (error) {
                console.error('Error updating step with cascade:', error);
                throw error;
            }
        },
        [updateStepWithCascadeMutation, excludeOrderId],
    );

    // ============================================================================
    // Helper Functions
    // ============================================================================

    /**
     * Merge schedule results from backend into existing steps
     */
    /**
     * Run full auto-schedule and return updated steps
     */
    const runFullAutoSchedule = useCallback(
        async (
            steps: ProductionPlanningStep[],
            direction: AutoScheduleDirection,
            startDate: Date | null,
            internalDeliveryDate: Date | null | undefined,
        ): Promise<ProductionPlanningStep[]> => {
            if (steps.length === 0) return steps;

            const input: AutoScheduleInput = {
                direction,
                steps: steps.map((s) => ({
                    stepId: s.stepId || '',
                    stepName: s.stepName,
                    estimatedProductionHours: s.estimatedProductionHours || 0,
                    estimatedMachineHours: s.estimatedMachineHours || 0,
                    allowOvernight: s.allowOvernight || false,
                    startNextDay: s.startNextDay || false,
                    departmentId: s.departmentId,
                    workUnits: s.workUnits,
                    machineId: s.machineId,
                })),
            };

            if (direction === AutoScheduleDirection.FORWARD_FROM_NOW) {
                if (!startDate) return steps;
                input.startDate = startDate;
            }

            if (direction === AutoScheduleDirection.BACKWARD_FROM_DELIVERY) {
                if (!internalDeliveryDate) return steps;
                input.deliveryDate = internalDeliveryDate;
            }

            const results = await autoScheduleProductionPlan(input);
            if (!results) return steps;

            return mergeScheduleResults(steps, results);
        },
        [autoScheduleProductionPlan, mergeScheduleResults],
    );

    /**
     * Handle step change with cascade and return updated steps
     */
    const handleStepChangeWithCascade = useCallback(
        async (
            steps: ProductionPlanningStep[],
            index: number,
            update: Partial<ProductionPlanningStep>,
        ): Promise<ProductionPlanningStep[]> => {
            // Apply the update to the step
            const updatedSteps = steps.map((step, i) => (i === index ? { ...step, ...update } : step));

            // Determine which property changed
            const changedProperty = (() => {
                if ('deadline' in update) return ChangedProperty.DEADLINE;
                if ('estimatedProductionHours' in update) return ChangedProperty.PRODUCTION_HOURS;
                if ('estimatedMachineHours' in update) return ChangedProperty.MACHINE_HOURS;
                if ('startDate' in update) return ChangedProperty.START_DATE;
                if ('allowOvernight' in update) return ChangedProperty.ALLOW_OVERNIGHT;
                if ('startNextDay' in update) return ChangedProperty.START_NEXT_DAY;
                return null;
            })();

            // Only cascade if a scheduling-related property changed
            if (!changedProperty) return updatedSteps;

            // Can't cascade without a start date on the changed step — return the update as-is
            if (!updatedSteps[index]?.startDate) return updatedSteps;

            const results = await updateStepWithCascade({
                steps: updatedSteps.map((step) => ({
                    stepId: step.stepId,
                    stepName: step.stepName,
                    startDate: step.startDate,
                    deadline: step.deadline,
                    estimatedProductionHours: step.estimatedProductionHours,
                    estimatedMachineHours: step.estimatedMachineHours,
                    stepOrder: step.stepOrder,
                    allowOvernight: step.allowOvernight,
                    startNextDay: step.startNextDay,
                    affectedByQuantityModifier: step.affectedByQuantityModifier,
                    workUnits: step.workUnits,
                    departmentId: step.departmentId,
                    machineId: step.machineId,
                })),
                changedStepIndex: index,
                changedProperty,
            });

            if (!results) return updatedSteps;

            return mergeScheduleResults(updatedSteps, results);
        },
        [updateStepWithCascade, mergeScheduleResults],
    );

    // ============================================================================
    // Validation Helpers
    // ============================================================================

    /**
     * Check if steps are in chronological order
     */
    // ============================================================================
    // Context Value
    // ============================================================================

    const value = useMemo(
        () => ({
            autoScheduleProductionPlan,
            calculateStepHours,
            updateStepWithCascade,
            runFullAutoSchedule,
            handleStepChangeWithCascade,
            mergeScheduleResults,
            validateStepsChronological,
            validateDeadline,
            validateStartDate,
            autoScheduling,
            calculatingHours,
            updatingWithCascade,
        }),
        [
            autoScheduleProductionPlan,
            calculateStepHours,
            updateStepWithCascade,
            runFullAutoSchedule,
            handleStepChangeWithCascade,
            mergeScheduleResults,
            validateStepsChronological,
            validateDeadline,
            validateStartDate,
            autoScheduling,
            calculatingHours,
            updatingWithCascade,
        ],
    );

    return <ProductionPlanningContext.Provider value={value}>{children}</ProductionPlanningContext.Provider>;
}

export function useProductionPlanning() {
    const context = useContext(ProductionPlanningContext);
    if (!context) {
        throw new Error('useProductionPlanning must be used within a ProductionPlanningProvider');
    }
    return context;
}
